//src/components/SectionWiseMachine.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import { useMediaQuery } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Button, IconButton } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import AddSectionWiseMachine from './AddSectionWiseMachine';
import EditSectionWiseMachine from './EditSectionWiseMachine';
import env from "react-dotenv";

const SectionWiseMachine = () => {
  const [machines, setMachines] = useState([]);
  const [machineDetails, setMachineDetails] = useState([]); // For resolved names
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);

  // Fetch all required data (machines, products, steps) and merge for display
  const fetchMachines = async () => {
    try {
      const [sectionwisemachinesResponse, machinesResponse, productsResponse, stepsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/sectionwisemachine`),
        axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/machinelist`),
        axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`),
        axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-steps`),
      ]);

      const sectionwisemachinesData = sectionwisemachinesResponse.data;
      const machinesData = machinesResponse.data;
      const productsData = productsResponse.data;
      const stepsData = stepsResponse.data;

      // Resolve names for machine, product, and step
      const resolvedMachines = sectionwisemachinesData.map((sectionwiseMachine, index) => {
        const matchedMachine = machinesData.find((m) => m.id === sectionwiseMachine.MachineId);
        const product = productsData.find((p) => p.id === sectionwiseMachine.ProductId);
        const step = stepsData.find((s) => s.step_id === sectionwiseMachine.StepId);
      
        return {
          id: sectionwiseMachine.id || index, // Use API-provided ID or fallback to index
          MachineName: matchedMachine ? matchedMachine.MachineName : 'N/A',
          ProductName: product ? product.product_name : 'N/A',
          StepName: step ? step.step_name : 'N/A',
          ShiftCapacity: sectionwiseMachine.ShiftCapacity || 'N/A',
          DayCapacity: sectionwiseMachine.DayCapacity || 'N/A',
          SectionName: sectionwiseMachine.SectionName || 'N/A',
        };
      });                

      setMachineDetails(resolvedMachines);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);

  const handleOpenAdd = () => {
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
  };

  const handleOpenEdit = (machine) => {
    setSelectedMachine(machine);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedMachine(null);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'SectionName', headerName: 'Section Name', width: 200 },
    { field: 'MachineName', headerName: 'Machine Name', width: 150 },
    { field: 'ProductName', headerName: 'Product Name', width: 150 },
    { field: 'StepName', headerName: 'Step Name', width: 150 },
    { field: 'ShiftCapacity', headerName: 'Shift Capacity', width: 150 },
    { field: 'DayCapacity', headerName: 'Day Capacity', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => handleOpenEdit(params.row)}>
          <Edit />
        </IconButton>
      ),
    },
  ];

  const isNonMobileScreens = useMediaQuery('(min-width: 1000px)');

  return (
    <Box style={{ height: 500, marginLeft: '0px', marginRight: '10px' }}>
      <h4>Section Wise Machine</h4>

      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenAdd}
        sx={{
          marginTop: '5px',
          marginBottom: '10px',
          backgroundColor: '#00308F',
          color: '#ffffff',
          padding: '3px 5px',
          '&:hover': {
            backgroundColor: '#00207E',
          },
        }}
      >
        Add New
      </Button>

      <Box
        sx={{
          height: 500,
          overflowY: 'auto',
        }}
      >
        <DataGrid
          rows={machineDetails}
          columns={columns}
          getRowId={(row) => row.id}
          // getRowId={(row) => `${row.MachineName}-${row.ProductName}-${row.StepName}`} // Combine fields to create a unique key
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          sx={{
            boxShadow: 1,
            border: 1,
            borderColor: 'primary.light',
            typography: 'subtitle2',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '.MuiDataGrid-columnHeaderTitle': {
              font: 'light important',
              overflow: 'visible !important',
            },
            '--DataGrid-overlayHeight': '300px',
          }}
        />
      </Box>

      <AddSectionWiseMachine open={openAdd} handleClose={handleCloseAdd} fetchMachines={fetchMachines} />
      {selectedMachine && (
        <EditSectionWiseMachine
          open={openEdit}
          handleClose={handleCloseEdit}
          fetchMachines={fetchMachines}
          machine={selectedMachine}
        />
      )}
    </Box>
  );
};

export default SectionWiseMachine;
